// Data + pure logic for the /scale-country sub-site — a little "travel map"
// where each stop is a scale to explore by ear. No audio and no React live
// here; see scaleCountryAudio.js for the Web Audio layer. Same posture as
// synthLab.js / keyboardPlan.js: structured, testable data plus small pure
// reducers for progress tracking.

export const STEPS = 16; // phrase grid columns
export const ROOT_MIDI = 60; // all v1 pages are rooted at C4

/* ============================================================
   Regions — the map's four neighborhoods
   ============================================================ */

export const REGIONS = [
  {
    id: "foothills",
    name: "Pentatonic Foothills",
    blurb: "Gentle, familiar ground — five notes, no wrong turns.",
  },
  {
    id: "plains",
    name: "Modal Plains",
    blurb: "The seven-note heartland where most of pop and rock quietly lives.",
  },
  {
    id: "coast",
    name: "The Weird Coast",
    blurb: "Exotic scales that wash in from somewhere else entirely.",
  },
  {
    id: "highlands",
    name: "Harmonic Highlands",
    blurb: "Higher, stranger ground for once the plains feel too easy.",
    fog: true,
  },
];

/* ============================================================
   Places — the 15 scales
   ============================================================ */

export const PLACES = [
  {
    id: "majpent",
    regionId: "foothills",
    name: "Major pentatonic",
    semis: [0, 2, 4, 7, 9],
    formula: "1 · 2 · 3 · 5 · 6",
    moodTag: "sunny, safe",
    mood: "the scale that never lets you down — every note lands somewhere friendly.",
    songRefs: ["\"My Girl\" — The Temptations", "\"Ring of Fire\" — Johnny Cash"],
    charNote: {
      text: "there's no 4th or 7th to create tension, so the 6 just drifts gently down to the 5 instead of straining anywhere.",
      from: 9,
      to: 7,
    },
    listenPhrase: [0, 2, 4, 7, 9, 7, 4, 0],
    brief: {
      starter:
        "Start here — five safe notes, nothing that can go wrong. Write a short phrase that just wanders and lands wherever it wants.",
      feedForward: null,
      sourceId: null,
    },
    map: { x: 60, y: 250 },
  },
  {
    id: "minpent",
    regionId: "foothills",
    name: "Minor pentatonic",
    semis: [0, 3, 5, 7, 10],
    formula: "1 · ♭3 · 4 · 5 · ♭7",
    moodTag: "bluesy, grounded",
    mood: "the first scale most guitarists ever learn, and the one they never fully put down.",
    songRefs: ["\"Sweet Child O' Mine\" — Guns N' Roses", "\"Stairway to Heaven\" solo — Led Zeppelin"],
    charNote: {
      text: "the ♭7 doesn't ache like a leading tone — it just falls home upward into the root, the oldest move in rock.",
      from: 10,
      to: 12,
    },
    listenPhrase: [0, 3, 5, 7, 10, 12, 10, 0],
    brief: {
      starter:
        "Minor pentatonic swaps in a ♭3 and a ♭7 that just falls home into the root — write a phrase that leans on that fall.",
      feedForward:
        "Take your Major pentatonic phrase and re-write it here — keep its shape, but let that ♭7 fall home into the root instead of drifting down to the 5.",
      sourceId: "majpent",
    },
    map: { x: 125, y: 272 },
  },
  {
    id: "blues",
    regionId: "foothills",
    name: "Blues",
    semis: [0, 3, 5, 6, 7, 10],
    formula: "1 · ♭3 · 4 · ♭5 · 5 · ♭7",
    moodTag: "smoky, crooked",
    mood: "minor pentatonic with one extra crooked note slipped in — that's the whole trick.",
    songRefs: ["\"The Thrill Is Gone\" — B.B. King", "\"Crossroads\" — Cream"],
    charNote: {
      text: "the ♭5 is passing spice, not a place to live — it's always on its way to the 5, never staying.",
      from: 6,
      to: 7,
    },
    listenPhrase: [0, 3, 5, 6, 7, 10, 7, 0],
    brief: {
      starter:
        "Blues is minor pentatonic with one crooked note slipped in — write a phrase that touches the ♭5 just long enough to feel it lean into the 5.",
      feedForward:
        "Take your Minor pentatonic phrase and re-write it here — keep its shape, but slip in that crooked ♭5 as a passing note on its way to the 5.",
      sourceId: "minpent",
    },
    map: { x: 190, y: 246 },
  },
  {
    id: "major",
    regionId: "plains",
    name: "Major (Ionian)",
    semis: [0, 2, 4, 5, 7, 9, 11],
    formula: "1 · 2 · 3 · 4 · 5 · 6 · 7",
    moodTag: "bright, resolved",
    mood: "the default setting — the scale every other scale gets described relative to.",
    songRefs: ["\"Twinkle Twinkle Little Star\"", "\"Ho Hey\" — The Lumineers"],
    charNote: {
      text: "the leading tone aches upward into the octave — that pull toward home is most of why major sounds \"finished\".",
      from: 11,
      to: 12,
    },
    listenPhrase: [0, 4, 7, 11, 12, 7, 4, 0],
    brief: {
      starter:
        "Major (Ionian) is the default setting — write a phrase that lets the leading tone ache up into the octave.",
      feedForward:
        "Take your Major pentatonic phrase and re-write it here — keep its shape, but fill in the 4th and 7th so that leading tone finally gets to ache home.",
      sourceId: "majpent",
    },
    map: { x: 275, y: 206 },
  },
  {
    id: "aeolian",
    regionId: "plains",
    name: "Aeolian",
    semis: [0, 2, 3, 5, 7, 8, 10],
    formula: "1 · 2 · ♭3 · 4 · 5 · ♭6 · ♭7",
    moodTag: "sad, default-minor",
    mood: "the natural minor — the sound your brain reaches for the moment a song turns sad.",
    songRefs: ["\"Losing My Religion\" — R.E.M.", "\"Zombie\" — The Cranberries"],
    charNote: {
      text: "the ♭6 sighs down onto the 5 — the default sadness, no drama required.",
      from: 8,
      to: 7,
    },
    listenPhrase: [0, 3, 7, 8, 7, 3, 0, null],
    brief: {
      starter:
        "Aeolian is the natural minor — write a phrase that lets the ♭6 sigh down onto the 5, no drama required.",
      feedForward:
        "Take your Minor pentatonic phrase and re-write it here — keep its shape, but add the 2nd and ♭6 so it can sigh properly toward the 5.",
      sourceId: "minpent",
    },
    map: { x: 340, y: 228 },
  },
  {
    id: "mixo",
    regionId: "plains",
    name: "Mixolydian",
    semis: [0, 2, 4, 5, 7, 9, 10],
    formula: "1 · 2 · 3 · 4 · 5 · 6 · ♭7",
    moodTag: "loose, funky",
    mood: "major with its collar loosened — that flatted 7th takes the edge off resolution.",
    songRefs: ["\"Sweet Home Alabama\" — Lynyrd Skynyrd", "\"Hey Jude\" (outro) — The Beatles"],
    charNote: {
      text: "the ♭7 refuses the ache of a leading tone and just slouches down to the 6 instead — basically all funk in one interval.",
      from: 10,
      to: 9,
    },
    listenPhrase: [0, 4, 7, 10, 9, 7, 4, 0],
    brief: {
      starter:
        "Mixolydian is major with its collar loosened — write a phrase that lets the ♭7 slouch down to the 6 instead of resolving up.",
      feedForward:
        "Take your Major phrase and re-write it here — keep its shape, but flatten that 7th so it slouches down to the 6 instead of resolving up.",
      sourceId: "major",
    },
    map: { x: 400, y: 196 },
  },
  {
    id: "dorian",
    regionId: "plains",
    name: "Dorian",
    semis: [0, 2, 3, 5, 7, 9, 10],
    formula: "1 · 2 · ♭3 · 4 · 5 · 6 · ♭7",
    moodTag: "cool, hopeful minor",
    mood: "minor with one window left open — that natural 6th keeps the room from going fully dark.",
    songRefs: ["\"Scarborough Fair\"", "\"Eleanor Rigby\" — The Beatles"],
    charNote: {
      text: "the natural 6 is one bright window in an otherwise minor room — it's the note that makes Dorian Dorian.",
      from: 9,
      to: 7,
    },
    listenPhrase: [0, 3, 7, 9, 7, 3, 0, null],
    brief: {
      starter:
        "Dorian is minor with one window left open — write a phrase that leans hard on that natural 6, the note that makes it Dorian.",
      feedForward:
        "Take your Aeolian phrase and re-write it here — keep its shape, but raise the ♭6 to a natural 6 and lean on it — that's the one window Dorian leaves open.",
      sourceId: "aeolian",
    },
    map: { x: 458, y: 218 },
  },
  {
    id: "lydian",
    regionId: "plains",
    name: "Lydian",
    semis: [0, 2, 4, 6, 7, 9, 11],
    formula: "1 · 2 · 3 · ♯4 · 5 · 6 · 7",
    moodTag: "floaty, wide-eyed",
    mood: "major with the roof taken off — that raised 4th gives everything a weightless, wondering quality.",
    songRefs: ["The Simpsons theme — Danny Elfman", "\"Dreams\" — Fleetwood Mac (verse)"],
    charNote: {
      text: "the ♯4 floats up to the 5 instead of pulling down toward the 3, which is exactly why Lydian never feels like it's straining.",
      from: 6,
      to: 7,
    },
    listenPhrase: [0, 4, 6, 7, 6, 4, 0, null],
    brief: {
      starter:
        "Lydian is major with the roof taken off — write a phrase that lets the ♯4 float up to the 5 instead of pulling down.",
      feedForward:
        "Take your Major phrase and re-write it here — keep its shape, but raise the 4th and let it float up to the 5 instead of resolving down.",
      sourceId: "major",
    },
    map: { x: 518, y: 188 },
  },
  {
    id: "phrygian",
    regionId: "plains",
    name: "Phrygian",
    semis: [0, 1, 3, 5, 7, 8, 10],
    formula: "1 · ♭2 · ♭3 · 4 · 5 · ♭6 · ♭7",
    moodTag: "dark, flamenco-adjacent",
    mood: "minor's darkest sibling — that ♭2 sitting right on top of home gives it a coiled, tense edge.",
    songRefs: ["\"Wherever I May Roam\" — Metallica", "Flamenco compás — traditional"],
    charNote: {
      text: "the ♭2 leans right on top of home, close enough to feel like a shadow of the root itself.",
      from: 1,
      to: 0,
    },
    listenPhrase: [0, 1, 0, 3, 5, 3, 1, 0],
    brief: {
      starter:
        "Phrygian is minor's darkest sibling — write a phrase that lets the ♭2 lean right on top of home like a shadow.",
      feedForward:
        "Take your Aeolian phrase and re-write it here — keep its shape, but flatten the 2nd into a ♭2 that leans on home like a shadow.",
      sourceId: "aeolian",
    },
    map: { x: 576, y: 210 },
  },
  {
    id: "phrygdom",
    regionId: "coast",
    name: "Phrygian dominant",
    semis: [0, 1, 4, 5, 7, 8, 10],
    formula: "1 · ♭2 · 3 · 4 · 5 · ♭6 · ♭7",
    moodTag: "fiery, exotic",
    mood: "Phrygian's spicier cousin — swap in a major 3rd and the ♭2 suddenly sounds like heat, not gloom.",
    songRefs: ["\"Misirlou\" — Dick Dale", "Klezmer freygish melodies — traditional"],
    charNote: {
      text: "the ♭2 sitting against a major 3rd is instant heat — that clash is the whole flavor of this scale.",
      from: 1,
      to: 0,
    },
    listenPhrase: [0, 1, 4, 5, 7, 8, 7, 0],
    brief: {
      starter:
        "Phrygian dominant swaps in a major 3rd against that ♭2 for instant heat — write a phrase that lets the clash burn.",
      feedForward:
        "Take your Phrygian phrase and re-write it here — keep its shape, but raise the ♭3 to a major 3rd and let it clash against that ♭2 for heat.",
      sourceId: "phrygian",
    },
    map: { x: 432, y: 106 },
  },
  {
    id: "hirajoshi",
    regionId: "coast",
    name: "Hirajoshi",
    semis: [0, 2, 3, 7, 8],
    formula: "1 · 2 · ♭3 · 5 · ♭6",
    moodTag: "still, twilight",
    mood: "a Japanese pentatonic that sounds like dusk — spare, a little melancholy, never in a hurry.",
    songRefs: ["Traditional koto repertoire", "\"Turning Japanese\" — The Vapors (intro gesture)"],
    charNote: {
      text: "the ♭6 leans hard into the 5 — koto at dusk, one soft resolution and nowhere else to go.",
      from: 8,
      to: 7,
    },
    listenPhrase: [0, 2, 3, 7, 8, 7, 3, 0],
    brief: {
      starter:
        "Hirajoshi sounds like dusk — write a phrase that lets the ♭6 lean hard into the 5, one soft resolution and nowhere else to go.",
      feedForward:
        "Take your Minor pentatonic phrase and re-write it here — keep its shape, but swap the 4th and ♭7 for a 2nd and ♭6 that lean quietly toward dusk.",
      sourceId: "minpent",
    },
    map: { x: 492, y: 80 },
  },
  {
    id: "wholetone",
    regionId: "coast",
    name: "Whole tone",
    semis: [0, 2, 4, 6, 8, 10],
    formula: "1 · 2 · 3 · ♯4 · ♯5 · ♭7",
    moodTag: "dreamlike, suspended",
    mood: "every step is exactly the same size, so your ear never finds a home to land on — pure dream-sequence material.",
    songRefs: ["\"Jimbo's Lullaby\" — Debussy", "Classic cartoon \"dream\" sting cues"],
    charNote: {
      text: "every step the same size means nothing ever resolves — the ♭6 drifts to the ♭7 the same weightless way every note drifts to the next.",
      from: 8,
      to: 10,
    },
    listenPhrase: [0, 2, 4, 6, 8, 10, 8, 4],
    brief: {
      starter:
        "Whole tone never lets your ear find home — write a phrase where every step drifts the same weightless distance.",
      feedForward:
        "Take your Lydian phrase and re-write it here — keep its shape, but stretch every step to the same whole-tone size so nothing ever resolves.",
      sourceId: "lydian",
    },
    map: { x: 552, y: 60 },
  },
  {
    id: "dblharm",
    regionId: "coast",
    name: "Double harmonic",
    semis: [0, 1, 4, 5, 7, 8, 11],
    formula: "1 · ♭2 · 3 · 4 · 5 · ♭6 · 7",
    moodTag: "ornate, ceremonial",
    mood: "two augmented-second gaps stacked into one scale — it sounds instantly \"elsewhere\", ornate and formal.",
    songRefs: ["\"Miserlou\"-adjacent Arabic maqam Hijaz Kar pieces", "\"Misirlou\" reharmonizations"],
    charNote: {
      text: "the ♭2 and the natural 7 pinch home from both sides at once — that double squeeze is the scale's whole personality.",
      from: 11,
      to: 12,
    },
    listenPhrase: [0, 1, 4, 5, 7, 8, 11, 12],
    brief: {
      starter:
        "Double harmonic pinches home from both sides at once — write a phrase that lets the ♭2 and natural 7 squeeze the root.",
      feedForward:
        "Take your Phrygian dominant phrase and re-write it here — keep its shape, but raise that ♭7 to a natural 7 so it pinches home from the other side too.",
      sourceId: "phrygdom",
    },
    map: { x: 612, y: 90 },
  },
  {
    id: "harmmin",
    regionId: "highlands",
    name: "Harmonic minor",
    semis: [0, 2, 3, 5, 7, 8, 11],
    formula: "1 · 2 · ♭3 · 4 · 5 · ♭6 · 7",
    moodTag: "dramatic, classical",
    mood: "natural minor with the plot twist ending — that raised 7th turns a sad scale into a dramatic one.",
    songRefs: ["\"Nothing Else Matters\" (bridge) — Metallica", "Classical minor-key cadences — traditional"],
    charNote: {
      text: "a raised 7th in an otherwise minor world is the plot twist — it demands resolution the way major's leading tone does, but from a darker room.",
      from: 11,
      to: 12,
    },
    listenPhrase: [0, 3, 7, 8, 11, 12, 8, 0],
    brief: {
      starter:
        "Harmonic minor is natural minor with a plot-twist ending — write a phrase that lets the raised 7th demand resolution from a darker room.",
      feedForward: null,
      sourceId: null,
    },
    map: { x: 150, y: 92 },
  },
  {
    id: "melmin",
    regionId: "highlands",
    name: "Melodic minor",
    semis: [0, 2, 3, 5, 7, 9, 11],
    formula: "1 · 2 · ♭3 · 4 · 5 · 6 · 7",
    moodTag: "shape-shifting",
    mood: "minor at the bottom, major on the way up — jazz's favorite scale for exactly that reason.",
    songRefs: ["\"Round Midnight\" — Thelonious Monk (changes)", "Bebop melodic-minor lines — jazz standards"],
    charNote: {
      text: "the raised 7th still resolves up like major's leading tone, but it's sitting over a minor 3rd — minor at the bottom, major on the way up.",
      from: 11,
      to: 12,
    },
    listenPhrase: [0, 3, 7, 9, 11, 12, 9, 0],
    brief: {
      starter:
        "Melodic minor is minor at the bottom, major on the way up — write a phrase that shape-shifts as it climbs.",
      feedForward: null,
      sourceId: null,
    },
    map: { x: 222, y: 66 },
  },
];

// Trail order through the non-highlands regions — the guided path a visitor
// walks before the fog over the Highlands lifts.
export const TRAIL = [
  "majpent",
  "minpent",
  "blues",
  "major",
  "aeolian",
  "mixo",
  "dorian",
  "lydian",
  "phrygian",
  "phrygdom",
  "hirajoshi",
  "wholetone",
  "dblharm",
];

export const getPlace = (id) => PLACES.find((p) => p.id === id) || null;

export const placesForRegion = (regionId) => PLACES.filter((p) => p.regionId === regionId);

export const getRegion = (id) => REGIONS.find((r) => r.id === id) || null;

/* ============================================================
   Scale helpers
   ============================================================ */

// Is this semitone offset (any midi offset, including 12 or negatives) in the
// scale? Always normalizes mod 12 first, so 12 -> 0, -1 -> 11, etc.
export const inScale = (semis, semitone) => {
  const s = ((semitone % 12) + 12) % 12;
  return semis.includes(s);
};

const NOTE_NAMES = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];

const noteLabel = (midi) => `${NOTE_NAMES[((midi % 12) + 12) % 12]}${Math.floor(midi / 12) - 1}`;

// Every scale note from rootMidi..rootMidi+12 inclusive (top octave root
// included), returned DESCENDING as { midi, label }.
export const rowsForScale = (semis, rootMidi = ROOT_MIDI) => {
  const rows = [];
  for (let midi = rootMidi; midi <= rootMidi + 12; midi += 1) {
    const offset = midi - rootMidi;
    if (inScale(semis, offset)) {
      rows.push({ midi, label: noteLabel(midi) });
    }
  }
  return rows.reverse();
};

/* ============================================================
   Phrase model — a phrase is STEPS columns, each a list of midi notes
   ============================================================ */

export const emptyPhrase = () => Array.from({ length: STEPS }, () => []);

// Immutably toggle `midi` in/out of a phrase column.
export const toggleStep = (phrase, stepIndex, midi) =>
  phrase.map((col, i) => {
    if (i !== stepIndex) return col;
    return col.includes(midi) ? col.filter((m) => m !== midi) : [...col, midi];
  });

export const phraseHasNotes = (phrase) => phrase.some((col) => col.length > 0);

// Flat array of midi numbers in step order — within a multi-note column,
// ascending. Used by the campfire echo game to check played-back notes.
export const phraseNoteSequence = (phrase) =>
  phrase.reduce((notes, col) => notes.concat([...col].sort((a, b) => a - b)), []);

/* ============================================================
   Progress model — pure reducers over { visited, phrases, phraseMeta }
   ============================================================ */

export const EMPTY_PROGRESS = { visited: {}, phrases: {}, phraseMeta: {} };

export const withVisit = (progress, placeId) => ({
  ...progress,
  visited: { ...progress.visited, [placeId]: true },
});

// A phrase with no notes removes the saved entry instead of storing an empty
// one, so placeState can tell "never saved" apart from "saved but cleared".
// Saving a (non-empty) phrase also (re)sets its echo metadata, so re-saving a
// place resets its campfire echo schedule; clearing a phrase clears the meta
// too.
export const withPhrase = (progress, placeId, phrase, now = Date.now()) => {
  const phrases = { ...progress.phrases };
  const phraseMeta = { ...progress.phraseMeta };
  if (phraseHasNotes(phrase)) {
    phrases[placeId] = phrase;
    phraseMeta[placeId] = { savedAt: now, lastEchoAt: null, echoCount: 0 };
  } else {
    delete phrases[placeId];
    delete phraseMeta[placeId];
  }
  return { ...progress, phrases, phraseMeta };
};

// Record a campfire echo attempt against an already-saved phrase: bumps
// echoCount and stamps lastEchoAt. No-op (returns progress unchanged) if the
// place has no saved phrase to echo.
export const withEcho = (progress, placeId, now = Date.now()) => {
  const phrase = progress.phrases[placeId];
  if (!phrase || !phraseHasNotes(phrase)) return progress;
  const existing = progress.phraseMeta[placeId] || { savedAt: now, lastEchoAt: null, echoCount: 0 };
  return {
    ...progress,
    phraseMeta: {
      ...progress.phraseMeta,
      [placeId]: { ...existing, lastEchoAt: now, echoCount: existing.echoCount + 1 },
    },
  };
};

export const placeState = (progress, placeId) => {
  if (progress.phrases[placeId] && phraseHasNotes(progress.phrases[placeId])) return "stamped";
  if (progress.visited[placeId]) return "visited";
  return "new";
};

export const regionProgress = (progress, regionId) => {
  const places = placesForRegion(regionId);
  const stamped = places.filter((p) => placeState(progress, p.id) === "stamped").length;
  return { stamped, total: places.length };
};

/* ============================================================
   Trail helpers
   ============================================================ */

// 0-based index of a place on the guided TRAIL, or -1 if it's not on it
// (e.g. a fogged-in Harmonic Highlands place).
export const trailIndexOf = (placeId) => TRAIL.indexOf(placeId);

// First TRAIL stop that isn't stamped yet, in trail order — the visitor's
// next suggested destination. Null once every trail stop is stamped.
export const nextTrailStop = (progress) => {
  const stop = TRAIL.find((id) => placeState(progress, id) !== "stamped");
  return stop === undefined ? null : stop;
};

/* ============================================================
   Campfire echo scheduling — spaced repetition over saved phrases
   ============================================================ */

// Days until the next echo prompt, indexed by how many echoes a phrase has
// already had (clamped to the last interval once it runs out).
export const ECHO_INTERVALS_DAYS = [1, 3, 7, 14, 30];

const DAY_MS = 24 * 60 * 60 * 1000;

// Is a stamped place due for a campfire echo right now? Places with no saved
// phrase are never due. A stamped place with missing meta (old v1 data, or
// pre-echo-feature saves) counts as due immediately.
export const isEchoDue = (progress, placeId, now = Date.now()) => {
  const phrase = progress.phrases[placeId];
  if (!phrase || !phraseHasNotes(phrase)) return false;
  const meta = progress.phraseMeta[placeId];
  if (!meta) return true;
  const echoCount = meta.echoCount || 0;
  const intervalDays = ECHO_INTERVALS_DAYS[Math.min(echoCount, ECHO_INTERVALS_DAYS.length - 1)];
  const base = Math.max(meta.savedAt, meta.lastEchoAt || 0);
  return now >= base + intervalDays * DAY_MS;
};

// All due place ids, trail places first (in TRAIL order), then any other
// stamped-and-due places (e.g. Harmonic Highlands) in PLACES order.
export const dueEchoes = (progress, now = Date.now()) => {
  const trailDue = TRAIL.filter((id) => isEchoDue(progress, id, now));
  const trailSet = new Set(TRAIL);
  const otherDue = PLACES.filter((p) => !trailSet.has(p.id) && isEchoDue(progress, p.id, now)).map(
    (p) => p.id
  );
  return [...trailDue, ...otherDue];
};

/* ============================================================
   Feed-forward briefs — the write-prompt shown before a phrase grid
   ============================================================ */

// The prompt to show before writing a phrase for `placeId`: feed-forward
// text built from an earlier saved phrase when one exists, otherwise the
// place's cold-start starter text.
export const briefFor = (progress, placeId) => {
  const place = getPlace(placeId);
  const brief = place && place.brief;
  if (!brief) return { text: "", sourceId: null, sourceName: null, sourcePhrase: null };

  const sourcePlace = brief.sourceId ? getPlace(brief.sourceId) : null;
  const sourcePhrase = sourcePlace ? progress.phrases[sourcePlace.id] : null;

  if (sourcePlace && sourcePhrase && phraseHasNotes(sourcePhrase)) {
    return {
      text: brief.feedForward,
      sourceId: sourcePlace.id,
      sourceName: sourcePlace.name,
      sourcePhrase,
    };
  }
  return { text: brief.starter, sourceId: null, sourceName: null, sourcePhrase: null };
};

/* ============================================================
   Persistence — thin localStorage wrappers around the pure model
   ============================================================ */

export const PROGRESS_KEY = "scaleCountryProgress";

// Tolerates v1 blobs (no phraseMeta key) by merging them onto the v2 shape;
// never throws.
export const loadProgress = () => {
  try {
    const raw = window.localStorage.getItem(PROGRESS_KEY);
    if (!raw) return { visited: {}, phrases: {}, phraseMeta: {} };
    const parsed = JSON.parse(raw);
    return {
      visited: (parsed && typeof parsed.visited === "object" && parsed.visited) || {},
      phrases: (parsed && typeof parsed.phrases === "object" && parsed.phrases) || {},
      phraseMeta: (parsed && typeof parsed.phraseMeta === "object" && parsed.phraseMeta) || {},
    };
  } catch {
    return { visited: {}, phrases: {}, phraseMeta: {} };
  }
};

export const saveProgress = (progress) => {
  try {
    window.localStorage.setItem(PROGRESS_KEY, JSON.stringify(progress));
  } catch {
    // ignore quota / privacy-mode errors — progress just won't persist
  }
};
